// <pos-payment-settings/> — 金流帳號申請教學 + 金鑰自助輸入（owner only）
// see architecture.md#L280（金流架構策略 v2.0 — BYOK 商家自有帳號模式）
//
// 設計原則：
//   - 錢直接進店家自己的綠界 / LINE Pay 帳戶，TAUKE 不代收不經手（零電支執照疑慮）
//   - 金鑰店家自行輸入 → server 端 vault 加密儲存（tenant_payment_credentials），
//     不經過客服、不經過任何人眼睛（Pat 決策 2026-06-13）
//   - 本頁同時是「申請導遊」：step-by-step + 外連官方申請頁 + 進度勾選（localStorage）

import { listPaymentCredentials, setPaymentCredentials, deletePaymentCredentials } from '../lib/api.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

const LS_KEY = 'tauke.payment-setup.progress';

const PROVIDERS = [
  {
    id: 'ecpay',
    icon: '💳',
    name: '綠界 ECPay',
    subtitle: '信用卡 / Apple Pay 收款',
    fee: '費率 2.75% + NT$1/筆 ｜ 免年費免設定費 ｜ 約 7 個工作天入帳',
    applyUrl: 'https://member.ecpay.com.tw/',
    applyLabel: '前往綠界註冊（member.ecpay.com.tw）',
    reviewTime: '審核約 3–7 個工作天',
    prepare: [
      '負責人身分證（個人賣家）或 公司統編 + 負責人身分證（公司戶）',
      '銀行存摺封面照片（撥款帳戶）',
      '店面照片或菜單（佐證實際營業）',
    ],
    steps: [
      { t: '註冊綠界會員', d: '點下方按鈕前往綠界官網，選「加入會員」→ 會員類型選「廠商」。完成 Email 與手機驗證。' },
      { t: '填寫商家資料', d: '登入後台填基本資料：店名、營業地址、負責人資訊、撥款銀行帳戶。個人賣家免統編也能申請。' },
      { t: '開通「信用卡收款」服務', d: '後台「服務申請」勾選信用卡一次付清。一般會員免費開通、費率 2.75%。送出後等綠界審核（3–7 個工作天，會發 Email 通知）。' },
      { t: '取得三把金鑰', d: '審核通過後，到後台「系統開發管理 → 系統介接設定」頁面，抄下 MerchantID、HashKey、HashIV 三個值。' },
      { t: '回到本頁填入金鑰', d: '把三個值貼進下方「金鑰設定」並儲存（系統加密保存，僅你可管理）。完成後建一筆 10 元測試訂單刷刷看，驗章成功會顯示「已驗證 ✓」。' },
    ],
    fields: [
      { key: 'merchant_id', label: '特店編號（MerchantID）', placeholder: '例：3002607（5–10 位數字）', secret: false, hint: '在哪裡找？登入綠界廠商後台，首頁右上角就寫著「特店編號」。' },
      { key: 'hash_key', label: '介接金鑰 Key（HashKey）', placeholder: '32 個英數字', secret: true, hint: '在哪裡找？綠界後台 → 系統開發管理 → 系統介接設定，「HashKey」整串複製過來。' },
      { key: 'hash_iv', label: '介接金鑰 IV（HashIV）', placeholder: '16 個英數字', secret: true, hint: '在哪裡找？跟上面同一頁，「HashIV」就在 HashKey 下面。' },
    ],
  },
  {
    id: 'linepay',
    icon: '🟢',
    name: 'LINE Pay',
    subtitle: '台灣用戶接受度最高的行動支付',
    fee: '費率 3.15%（含稅）｜ 免月租免設定費 ｜ 約 2 個工作天入帳',
    applyUrl: 'https://pay.line.me/tw/',
    applyLabel: '前往 LINE Pay 官網申請（pay.line.me/tw）',
    reviewTime: '審核約 1–2 週（建議越早送件越好）',
    prepare: [
      '公司或商業登記統編（LINE Pay 需要登記證明，個人無法申請）',
      '負責人身分證 + 銀行帳戶',
      '店面照片、菜單或官網（佐證營業內容）',
    ],
    steps: [
      { t: '送出商家申請', d: '點下方按鈕前往 LINE Pay 官網，點「商家申請」。填寫商家資訊並上傳商業登記、身分證、帳戶文件。' },
      { t: '等待審核', d: '審核約 1–2 週，LINE Pay 會 Email 通知結果。期間可先用現金收款，POS 功能不受影響。' },
      { t: '取得兩把金鑰', d: '審核通過後，登入 LINE Pay 商家中心 →「管理介接金鑰」，抄下 Channel ID 與 Channel Secret。' },
      { t: '回到本頁填入金鑰', d: '把兩個值貼進下方「金鑰設定」並儲存（系統加密保存）。完成後建一筆 10 元測試訂單用 LINE Pay 付付看，成功會顯示「已驗證 ✓」。' },
    ],
    fields: [
      { key: 'channel_id', label: '通路編號（Channel ID）', placeholder: '例：1657xxxxxx（8–12 位數字）', secret: false, hint: '在哪裡找？LINE Pay 商家中心 → 管理介接金鑰，第一行就是。' },
      { key: 'channel_secret', label: '通路密鑰（Channel Secret）', placeholder: '32 個英數字', secret: true, hint: '在哪裡找？同一頁按「查看」就會顯示，整串複製過來。' },
    ],
  },
];

class PosPaymentSettings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.progress = this.loadProgress();
    this.saved = {};        // provider → { cred_hint, verified_at, ... }（來自 server）
    this.busy = {};         // provider → true（儲存/移除中）
    this.formError = {};    // provider → 錯誤訊息
    this.loading = true;
  }

  connectedCallback() {
    this.render();
    this.loadStatus();
  }

  async loadStatus() {
    try {
      const { credentials } = await listPaymentCredentials();
      this.saved = Object.fromEntries((credentials || []).map((c) => [c.provider, c]));
    } catch (err) {
      console.warn('[payment-settings] load status failed', err);
    }
    this.loading = false;
    this.render();
  }

  loadProgress() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; }
    catch { return {}; }
  }

  saveProgress() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(this.progress)); }
    catch { /* private mode 等存不了就算了，純 UX 輔助 */ }
  }

  toggleStep(providerId, idx) {
    const key = `${providerId}.${idx}`;
    this.progress[key] = !this.progress[key];
    this.saveProgress();
    this.render();
  }

  doneCount(p) {
    return p.steps.filter((_, i) => this.progress[`${p.id}.${i}`]).length;
  }

  async saveCredentials(provider) {
    const p = PROVIDERS.find((x) => x.id === provider);
    const creds = {};
    for (const f of p.fields) {
      const input = this.shadowRoot.querySelector(`#cred-${provider}-${f.key}`);
      creds[f.key] = (input?.value || '').trim();
      if (!creds[f.key]) {
        this.formError[provider] = `請填寫 ${f.label}`;
        this.render();
        return;
      }
    }
    this.busy[provider] = true;
    this.formError[provider] = null;
    this.render();
    try {
      const { credentials } = await setPaymentCredentials(provider, creds);
      this.saved = Object.fromEntries((credentials || []).map((c) => [c.provider, c]));
    } catch (err) {
      this.formError[provider] = err.message_zh || err.message || '儲存失敗';
    }
    this.busy[provider] = false;
    this.render();
  }

  async removeCredentials(provider) {
    if (!confirm('確定移除這組金鑰？移除後該付款方式將暫停，可隨時重新填入。')) return;
    this.busy[provider] = true;
    this.render();
    try {
      const { credentials } = await deletePaymentCredentials(provider);
      this.saved = Object.fromEntries((credentials || []).map((c) => [c.provider, c]));
    } catch (err) {
      this.formError[provider] = err.message_zh || err.message || '移除失敗';
    }
    this.busy[provider] = false;
    this.render();
  }

  renderCredBlock(p) {
    const saved = this.saved[p.id];
    const busy = this.busy[p.id];
    const error = this.formError[p.id];

    if (this.loading) return `<div class="cred-loading">讀取設定中…</div>`;

    if (saved) {
      return `
        <div class="cred-saved" data-testid="cred-saved-${p.id}">
          <div>
            <span class="cred-badge ${saved.verified_at ? 'is-verified' : ''}">
              ${saved.verified_at ? '已驗證 ✓' : '已設定（待首筆交易驗證）'}
            </span>
            <span class="cred-hint">${escapeHtml(saved.cred_hint)}</span>
            ${saved.last_error ? `<div class="cred-err">⚠ ${escapeHtml(saved.last_error)}</div>` : ''}
          </div>
          <button class="btn-ghost" data-remove="${p.id}" ${busy ? 'disabled' : ''} data-testid="cred-remove-${p.id}">
            ${busy ? '處理中…' : '移除'}
          </button>
        </div>
        <div class="cred-note">金鑰已加密儲存（與 AI Keys 相同的 BYOK 安全機制），任何人——包括 TAUKE 團隊——都看不到原始值。要更換直接移除後重填。</div>
      `;
    }

    return `
      ${p.fields.map((f) => `
        <label class="cred-field">
          <span>${escapeHtml(f.label)}</span>
          <input id="cred-${p.id}-${f.key}" type="${f.secret ? 'password' : 'text'}"
                 autocomplete="off" spellcheck="false"
                 placeholder="${escapeHtml(f.placeholder)}" data-testid="cred-input-${p.id}-${f.key}" />
          ${f.hint ? `<small class="cred-hint">💡 ${escapeHtml(f.hint)}</small>` : ''}
        </label>
      `).join('')}
      ${error ? `<div class="cred-err" data-testid="cred-err-${p.id}">⚠ ${escapeHtml(error)}</div>` : ''}
      <button class="btn-save" data-save="${p.id}" ${busy ? 'disabled' : ''} data-testid="cred-save-${p.id}">
        ${busy ? '加密儲存中…' : '🔒 加密儲存'}
      </button>
      <div class="cred-note">金鑰用最高等級加密保存，連 TAUKE 團隊都看不到；之後畫面上也不會再顯示，只會標「已設定 ✓」。</div>
    `;
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 760px; margin: 0 auto; font-family: var(--font-sans); color: var(--fg-base); }
        h2 { font-size: 1.2rem; margin: 0 0 var(--s-2); }
        .lead { color: var(--fg-muted); font-size: .9rem; line-height: 1.7; margin-bottom: var(--s-5); }
        .lead strong { color: var(--brand-primary); }

        .why { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-md); padding: var(--s-4); font-size: .85rem; line-height: 1.7; color: var(--fg-muted); margin-bottom: var(--s-5); }
        .why b { color: var(--fg-base); }

        .provider { border: 1px solid var(--border); border-radius: var(--r-lg); margin-bottom: var(--s-5); overflow: hidden; background: var(--bg-elevated); }
        .provider__head { padding: var(--s-4); display: flex; align-items: center; gap: var(--s-3); border-bottom: 1px solid var(--border); }
        .provider__icon { font-size: 1.6rem; }
        .provider__title { font-weight: 700; font-size: 1.05rem; }
        .provider__sub { font-size: .8rem; color: var(--fg-dim); }
        .provider__fee { margin-left: auto; font-size: .75rem; color: var(--fg-dim); text-align: right; line-height: 1.5; }
        .provider__progress { font-size: .75rem; color: var(--brand-primary); font-weight: 600; }

        .block { padding: var(--s-4); }
        .block + .block { border-top: 1px dashed var(--border); }
        .block h4 { margin: 0 0 var(--s-3); font-size: .9rem; }

        .prep { margin: 0; padding-left: 1.2em; font-size: .85rem; color: var(--fg-muted); line-height: 1.8; }

        .step { display: flex; gap: var(--s-3); padding: var(--s-3); border-radius: var(--r-md); cursor: pointer; }
        .step:hover { background: var(--bg-base); }
        .step__check { width: 26px; height: 26px; min-width: 26px; border: 2px solid var(--border-strong); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: .8rem; margin-top: 2px; }
        .step[data-done="true"] .step__check { background: var(--brand-primary); border-color: var(--brand-primary); color: var(--fg-on-brand); }
        .step[data-done="true"] .step__t { text-decoration: line-through; color: var(--fg-dim); }
        .step__t { font-weight: 600; font-size: .9rem; margin-bottom: 2px; }
        .step__d { font-size: .82rem; color: var(--fg-muted); line-height: 1.6; }

        .apply-btn { display: inline-flex; align-items: center; gap: 8px; min-height: var(--touch-cta); padding: 0 var(--s-5); background: var(--brand-primary); color: var(--fg-on-brand); border-radius: var(--r-md); font-weight: 600; font-size: .95rem; text-decoration: none; margin-top: var(--s-2); }
        .review-time { font-size: .78rem; color: var(--fg-dim); margin-top: var(--s-2); }

        .cred-field { display: block; margin-bottom: var(--s-3); }
        .cred-field span { display: block; font-size: .8rem; font-weight: 600; margin-bottom: 4px; }
        .cred-field input { width: 100%; box-sizing: border-box; padding: var(--s-3); background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-sm); font-family: var(--font-mono, monospace); font-size: .9rem; }
        .cred-field input:focus { outline: none; border-color: var(--brand-primary); }
        .btn-save { min-height: var(--touch-cta); padding: 0 var(--s-5); background: var(--brand-primary); color: var(--fg-on-brand); border: 0; border-radius: var(--r-md); font-weight: 600; font-size: .95rem; cursor: pointer; font-family: inherit; }
        .btn-save[disabled] { opacity: .5; cursor: not-allowed; }
        .btn-ghost { min-height: 40px; padding: 0 var(--s-4); background: transparent; color: var(--fg-muted); border: 1px solid var(--border); border-radius: var(--r-md); cursor: pointer; font-family: inherit; }
        .cred-saved { display: flex; align-items: center; justify-content: space-between; gap: var(--s-3); }
        .cred-badge { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: .78rem; font-weight: 600; background: var(--warning-bg, #fff3cd); color: var(--warning-fg, #856404); }
        .cred-badge.is-verified { background: var(--success-bg, #d4edda); color: var(--success-fg, #155724); }
        .cred-hint { margin-left: var(--s-2); font-family: var(--font-mono, monospace); font-size: .82rem; color: var(--fg-muted); }
        .cred-err { color: var(--danger-fg, #721c24); font-size: .82rem; margin: var(--s-2) 0; }
        .cred-note { font-size: .75rem; color: var(--fg-dim); line-height: 1.6; margin-top: var(--s-3); }
        .cred-loading { color: var(--fg-dim); font-size: .85rem; }

        .back { display: inline-block; margin-bottom: var(--s-4); font-size: .85rem; color: var(--fg-muted); text-decoration: none; }
      </style>

      <a class="back" href="#/settings">← 回設定</a>
      <h2>💰 收款帳號設定</h2>
      <p class="lead">TAUKE 採 <strong>「商家自有帳號」</strong> 模式：客人付的錢<strong>直接進你的戶頭</strong>，
      TAUKE 不代收、不經手、不另抽成。申請好帳號後，把金鑰填入下方即可開始收款。</p>

      <div class="why">
        <b>為什麼要自己申請？</b><br>
        ① 錢直接進你的銀行帳戶（綠界約 7 個工作天、LINE Pay 約 2 個工作天入帳），不卡在平台。
        ② 對帳單、發票、金流後台都是你自己的，換系統也帶得走。
        ③ 申請全程免費，TAUKE 不收任何金流手續費以外的費用。
      </div>

      ${PROVIDERS.map((p) => `
        <div class="provider" data-testid="provider-${p.id}">
          <div class="provider__head">
            <div class="provider__icon">${p.icon}</div>
            <div>
              <div class="provider__title">${escapeHtml(p.name)}</div>
              <div class="provider__sub">${escapeHtml(p.subtitle)}</div>
            </div>
            <div class="provider__fee">
              ${escapeHtml(p.fee)}<br>
              <span class="provider__progress" data-testid="progress-${p.id}">進度 ${this.doneCount(p)}/${p.steps.length}</span>
            </div>
          </div>

          <div class="block">
            <h4>📋 申請前準備</h4>
            <ul class="prep">${p.prepare.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul>
          </div>

          <div class="block">
            <h4>🪜 申請步驟（點一下可勾選進度）</h4>
            ${p.steps.map((s, i) => `
              <div class="step" data-provider="${p.id}" data-idx="${i}" data-done="${!!this.progress[`${p.id}.${i}`]}" data-testid="step-${p.id}-${i}">
                <div class="step__check">${this.progress[`${p.id}.${i}`] ? '✓' : (i === this.doneCount(p) ? '▶' : i + 1)}</div>
                <div>
                  <div class="step__t">${escapeHtml(s.t)}</div>
                  <div class="step__d">${escapeHtml(s.d)}</div>
                </div>
              </div>
            `).join('')}
            <a class="apply-btn" href="${p.applyUrl}" target="_blank" rel="noopener noreferrer" data-testid="apply-${p.id}">
              🔗 ${escapeHtml(p.applyLabel)}
            </a>
            <div class="review-time">⏱ ${escapeHtml(p.reviewTime)}</div>
          </div>

          <div class="block">
            <h4>🔑 金鑰設定</h4>
            ${this.renderCredBlock(p)}
          </div>
        </div>
      `).join('')}
    `;

    this.shadowRoot.querySelectorAll('.step').forEach((el) => {
      el.addEventListener('click', () => this.toggleStep(el.dataset.provider, Number(el.dataset.idx)));
    });
    this.shadowRoot.querySelectorAll('[data-save]').forEach((el) => {
      el.addEventListener('click', () => this.saveCredentials(el.dataset.save));
    });
    this.shadowRoot.querySelectorAll('[data-remove]').forEach((el) => {
      el.addEventListener('click', () => this.removeCredentials(el.dataset.remove));
    });
  }
}

customElements.define('pos-payment-settings', PosPaymentSettings);
